import { Card, CardContent, Grid, IconButton, Paper, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Tooltip, Typography } from "@mui/material";
import TimelineIcon from "@mui/icons-material/Timeline";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import RemoveIcon from "@mui/icons-material/Remove";
import WarningIcon from "@mui/icons-material/Warning";

import { useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";

const TabPanel = ({ children, value, index }) => {
    return (
        <div role="tabpanel" hidden={value !== index}>
            {value === index && <div>{children}</div>}
        </div>
    );
};

export const TareasCalendar = ({
    elementoActivo,
    analisis,
    parametrosFiltrados,
    handleSeleccionarAnalisis,
    descargarPdf,
    handleTabElementosGeneralChange,
    handleTabElementoGeneralClick,
    tabActivoElementosGeneral,
    elementosGeneral,
    elementoGeneralSeleccionado,
    clienteSeleccionado,
    fechaInicioOferta,
    fechaFinOferta
}) => {
    const navigate = useNavigate();

    const topScrollRef = useRef(null);
    const contentScrollRef = useRef(null);
    const [dummyWidth, setDummyWidth] = useState(0);
    const syncingRef = useRef(false); // evita bucles de scroll

    // Ajusta el ancho del "dummy" al ancho real del contenido
    useEffect(() => {
        const el = contentScrollRef.current;
        if (!el) return;

        const update = () => setDummyWidth(el.scrollWidth);
        update();

        // Por si cambia el contenido dinámicamente
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const syncScroll = (source) => {
        const top = topScrollRef.current;
        const content = contentScrollRef.current;
        if (!top || !content || syncingRef.current) return;

        syncingRef.current = true;
        if (source === "top") {
            content.scrollLeft = top.scrollLeft;
        } else {
            top.scrollLeft = content.scrollLeft;
        }
        // pequeño delay para soltar el flag
        requestAnimationFrame(() => (syncingRef.current = false));
    };

    // "2024-11-07T00:00:00" (hora local, sin milisegundos, sin 'Z')
    const formatForApiLocalMidnight = (d) => {
        const pad = (n) => String(n).padStart(2, "0");
        const dt = new Date(d);
        return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T00:00:00`;
    };

    // "7/11/2024" (para mostrar en UI, opcional)
    const formatForUI = (d) => new Date(d).toLocaleDateString("es-ES");

    // NBG INI: Calcular meses a mostrar según periodo de oferta
    const getMesesAMostrar = () => {
        // Si no hay fechas de oferta, mostrar los 12 meses normales
        if (!fechaInicioOferta || !fechaFinOferta) {
            return [
                { mes: 0, nombre: "Ene" },
                { mes: 1, nombre: "Feb" },
                { mes: 2, nombre: "Mar" },
                { mes: 3, nombre: "Abr" },
                { mes: 4, nombre: "May" },
                { mes: 5, nombre: "Jun" },
                { mes: 6, nombre: "Jul" },
                { mes: 7, nombre: "Ago" },
                { mes: 8, nombre: "Sep" },
                { mes: 9, nombre: "Oct" },
                { mes: 10, nombre: "Nov" },
                { mes: 11, nombre: "Dic" }
            ];
        }

        // Calcular meses del periodo de oferta
        const inicio = new Date(fechaInicioOferta);
        const fin = new Date(fechaFinOferta);
        const mesesNombres = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];
        const meses = [];

        let fechaActual = new Date(inicio.getFullYear(), inicio.getMonth(), 1);
        const fechaFinal = new Date(fin.getFullYear(), fin.getMonth(), 1);

        while (fechaActual <= fechaFinal) {
            meses.push({
                mes: fechaActual.getMonth(),
                nombre: mesesNombres[fechaActual.getMonth()],
                anio: fechaActual.getFullYear()
            });
            fechaActual.setMonth(fechaActual.getMonth() + 1);
        }

        return meses;
    };

    const mesesAMostrar = getMesesAMostrar();
    // NBG FIN

    // Asume que 'fechaLimite' es una prop o una variable disponible.

    const getTooltipMessage = (fecha, tipo) => {

        if (fecha === 0) {
            return "Click para crear un nuevo informe";
        } else if (fecha === 3) {
            return "Click para subir PDF";
        } else if (tipo === 1) {
            return "";
        } else {
            return "Click para descargar PDF"; // Mensaje por defecto
        }
    };

    return (
        <>
            <Card
                style={{ height: "600px", display: "flex", flexDirection: "column" }}
            >
                <CardContent
                    ref={contentScrollRef}
                    sx={{
                        overflowX: "auto",
                        overflowY: "auto",
                        maxHeight: "100%",
                        whiteSpace: "nowrap",
                        flex: 1,
                        padding: 0,
                        "&:last-child": {
                            paddingBottom: 0
                        }
                    }}
                    onScroll={() => syncScroll("content")}
                >
                    <Grid
                        container
                        spacing={0}
                        sx={{
                            mb: 0,
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: 2
                        }}
                    >
                        <Grid item>
                            <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: "10px" }}>
                                <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center' }}>
                                    Calendario de tareas:
                                </Typography>
                                <h4 style={{ margin: 0 }}>
                                    {elementoActivo.nombre}
                                </h4>
                                {
                                    fechaInicioOferta && fechaFinOferta && (() => {
                                        const mesesNombres = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];

                                        const anioInicio = fechaInicioOferta.slice(0, 4);
                                        const mesInicioIndex = parseInt(fechaInicioOferta.slice(5, 7), 10) - 1;
                                        const nombreMesInicio = mesesNombres[mesInicioIndex];

                                        const anioFin = fechaFinOferta.slice(0, 4);
                                        const mesFinIndex = parseInt(fechaFinOferta.slice(5, 7), 10) - 1;
                                        const nombreMesFin = mesesNombres[mesFinIndex];

                                        return (
                                            <span style={{ fontSize: "1rem", whiteSpace: "nowrap" }}>
                                                {anioInicio === anioFin
                                                    ? `${nombreMesInicio} - ${nombreMesFin} ${anioInicio}`
                                                    : `${nombreMesInicio} ${anioInicio} - ${nombreMesFin} ${anioFin}`
                                                }
                                            </span>
                                        );
                                    })()
                                }
                            </div>
                        </Grid>
                    </Grid>

                    <Grid item xs={12} sx={{ padding: 2, paddingTop: 0 }}>
                        {elementoActivo.nombre ? (
                            <TableContainer
                                component={Paper}
                                style={{
                                    overflowX: "unset",
                                    minWidth: 1450,
                                }}
                            >
                                <Table sx={{ minWidth: 1200 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                sx={{
                                                    position: "sticky",
                                                    left: -20,
                                                    zIndex: 3,
                                                    backgroundColor: "white", // importante para que no se transparente
                                                    width: "50px",
                                                    px: 1
                                                }}
                                            ></TableCell>
                                            <TableCell
                                                align="left"
                                                sx={{
                                                    position: "sticky",
                                                    left: 50, // ancho acumulado de la primera col
                                                    zIndex: 2,
                                                    backgroundColor: "white",
                                                    width: "150px"
                                                }}
                                            >
                                                Tipo de análisis
                                            </TableCell>
                                            {/* NBG INI: Cabeceras dinámicas según periodo de oferta */}
                                            {mesesAMostrar.map((mesInfo, index) => (
                                                <TableCell key={index} sx={{ width: "100px" }}>
                                                    {mesInfo.nombre}
                                                </TableCell>
                                            ))}
                                            {/* NBG FIN */}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {elementoActivo.nombre &&
                                            // Mapeamos todos los parametros
                                            analisis.map((row) => {
                                                // row -> id, nombre
                                                // tareasFiltradas -> analisis, elemento
                                                var currentTime = new Date();

                                                // Obtenemos todos los valores del parametro actual (valores del mismo parametro, enero, febrero, ...)
                                                const valoresPorTarea =
                                                    parametrosFiltrados.filter(
                                                        (analisis) =>
                                                            parseInt(analisis.analisis, 10) === row.id, ""
                                                    );
                                                
                                                // NBG INI: Arrays dinámicos según meses de oferta
                                                let fechas = new Array(mesesAMostrar.length).fill(-1);
                                                let valorFechas = new Array(mesesAMostrar.length).fill(-1);
                                                let valorFechasRealizado = new Array(mesesAMostrar.length).fill(-1);
                                                // NBG FIN

                                                if (valoresPorTarea.length > 0) {
                                                    valoresPorTarea.map((val) => {
                                                        const fecha = new Date(val.fecha);

                                                        // NBG INI: Buscar coincidencia por mes y año
                                                        /*
                                                        const indice = mesesAMostrar.findIndex(
                                                            mesInfo => mesInfo.mes === fecha.getMonth() && mesInfo.anio === fecha.getFullYear()
                                                        );
                                                        */
                                                        const indice = mesesAMostrar.findIndex(
                                                            mesInfo => mesInfo.mes === fecha.getMonth() && mesInfo.anio === fecha.getFullYear()
                                                        );

                                                        if (indice !== -1) {
                                                            valorFechas[indice] = fecha;

                                                            if (val.incorrecto || val.noValido) {
                                                                fechas[indice] = 2;
                                                                valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                            } else if (val.realizado && val.pdf) {
                                                                // Marcar como realizado (verde) si se ha marcado realizado (1 VERDE = PDF)
                                                                fechas[indice] = 1;
                                                                valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                            } else if (val.realizado && row.tipo === 1) {
                                                                // Si está realizado y es tipo 1, mostrar verde aunque no tenga PDF
                                                                fechas[indice] = 1;
                                                                valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                            } else if ((val.pdf === null || val.pdf === 0) && val.realizado && row.tipo !== 1) {
                                                                // Si solo está recogido pero no realizado, mantener el azul (3 AZUL = NO PDF)
                                                                // Solo para tipo 0 y 2
                                                                fechas[indice] = 3;
                                                                valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                            } else {
                                                                fechas[indice] = 0;
                                                            }
                                                        }
                                                        // NBG FIN
                                                    });
                                                }

                                                // Devolvemos los valores
                                                return (
                                                    valoresPorTarea.length > 0 && (
                                                        <TableRow
                                                            key={row.id}
                                                            sx={{
                                                                "&:last-child td, &:last-child th": {
                                                                    border: 0,
                                                                },
                                                            }}
                                                        >
                                                            <TableCell
                                                                sx={{
                                                                    position: "sticky",
                                                                    left: -20,
                                                                    zIndex: 1,
                                                                    backgroundColor: "white"
                                                                }}
                                                            >
                                                                <Tooltip title="Ver parametros del elemento" placement="top">
                                                                    <IconButton onClick={() => handleSeleccionarAnalisis(row.id)}>
                                                                        <TimelineIcon />
                                                                    </IconButton>
                                                                </Tooltip>
                                                            </TableCell>
                                                            <TableCell
                                                                align="left"
                                                                sx={{
                                                                    position: "sticky",
                                                                    left: 50, // igual que en el header
                                                                    zIndex: 1,
                                                                    backgroundColor: "white"
                                                                }}
                                                            >
                                                                {row.nombre}
                                                            </TableCell>
                                                            {fechas.map((fecha, index) => (
                                                                <TableCell key={index}>
                                                                    <Tooltip title={getTooltipMessage(fecha, row.tipo)}>
                                                                        <span>
                                                                            <IconButton
                                                                                onClick={(e) => {
                                                                                    if (fecha === 0) {
                                                                                        // caso error → navegar a Mantenimiento Técnico
                                                                                        console.log("Navegar a Mantenimiento Técnico con:", {
                                                                                            idElemento: elementoActivo.nombre,
                                                                                            idAnalisis: row.id,
                                                                                        });
                                                                                        navigate("/mantenimientotecnico", {
                                                                                            state: {
                                                                                                idElemento: elementoActivo.id,
                                                                                                idAnalisis: row.id,
                                                                                                fecha: formatForApiLocalMidnight(valorFechas[index]), // "YYYY-MM-DDT00:00:00"
                                                                                                fechaIso: formatForUI(valorFechas[index]),
                                                                                                nombreAnalisis: row.nombre,
                                                                                                nombreElemento: elementoActivo.descripcion ||
                                                                                                    (elementoActivo.nombre !== "undefined" ? elementoActivo.nombre : null),
                                                                                                codigoCliente: clienteSeleccionado.codigoCliente,
                                                                                                nombreCliente: clienteSeleccionado.nombreCliente,
                                                                                                oferta: clienteSeleccionado.oferta
                                                                                            }
                                                                                        });
                                                                                    } else if (fecha === 3) { // <-- Corregido: else if
                                                                                        navigate("/visualizacion", {
                                                                                            state: {
                                                                                                idElemento: elementoGeneralSeleccionado.id,
                                                                                                idAnalisis: row.id,
                                                                                                fecha: formatForApiLocalMidnight(valorFechas[index]), // "YYYY-MM-DDT00:00:00"
                                                                                                fechaIso: formatForUI(valorFechas[index]),
                                                                                                nombreAnalisis: row.nombre,
                                                                                                nombreElemento: elementoGeneralSeleccionado.descripcion !== "" ? elementoGeneralSeleccionado.nombre + " " + elementoGeneralSeleccionado.descripcion : elementoGeneralSeleccionado.nombre + " " + elementoGeneralSeleccionado.numero,
                                                                                                codigoCliente: clienteSeleccionado.codigoCliente,
                                                                                                nombreCliente: clienteSeleccionado.nombreCliente,
                                                                                                oferta: clienteSeleccionado.oferta
                                                                                            }
                                                                                        });
                                                                                    } else {
                                                                                        // click downloads the PDF
                                                                                        descargarPdf(row.id, valorFechas[index]);
                                                                                    }
                                                                                }}
                                                                                sx={{
                                                                                    color: fecha === 3 ? '#2196f3' : undefined, // azul solo cuando fecha === 3
                                                                                    '&.Mui-disabled': (row.tipo === 1 && fecha === 1) ? { color: 'success.main' } : {}
                                                                                }}
                                                                                color={
                                                                                    fecha === -1
                                                                                        ? "primary"
                                                                                        : fecha === 0
                                                                                            ? "error"
                                                                                            : fecha === 2
                                                                                                ? "warning"
                                                                                                : fecha === 1
                                                                                                    ? "success"
                                                                                                    : undefined
                                                                                }
                                                                                disabled={fecha === -1 || (row.tipo === 1 && fecha === 1)}
                                                                            >

                                                                                {fecha === -1 ? (
                                                                                    <RemoveIcon />
                                                                                ) : fecha === 0 ? (
                                                                                    <ClearIcon />
                                                                                ) : fecha === 2 ? (
                                                                                    <WarningIcon />
                                                                                ) : fecha === 3 ? (
                                                                                    <CheckIcon />
                                                                                ) : (
                                                                                    <CheckIcon />
                                                                                )}

                                                                            </IconButton>
                                                                        </span>
                                                                    </Tooltip>
                                                                    {fecha === -1 ? (
                                                                        <p></p>
                                                                    ) : valorFechasRealizado[index] ===
                                                                        -1 ? (
                                                                        <p>
                                                                            {valorFechas[index] !== -1
                                                                                ? valorFechas[
                                                                                    index
                                                                                ].toLocaleDateString()
                                                                                : ""}
                                                                        </p>
                                                                    ) : (
                                                                        <p>
                                                                            {valorFechasRealizado[index] !== -1
                                                                                ? valorFechasRealizado[
                                                                                    index
                                                                                ].toLocaleDateString()
                                                                                : ""}
                                                                        </p>
                                                                    )}
                                                                    {/* <p>{valorFechas[index] != -1 ? valorFechas[index].toLocaleDateString() : ""}</p> */}
                                                                </TableCell>
                                                            ))}
                                                        </TableRow>
                                                    )
                                                );
                                            })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : elementosGeneral.length > 0 ? (
                            <Grid item xs={12}>
                                <Tabs
                                    value={tabActivoElementosGeneral}
                                    onChange={handleTabElementosGeneralChange}
                                    variant="scrollable"
                                    scrollButtons="auto"
                                >
                                    {elementosGeneral
                                        .sort((a, b) => {
                                            // Comparar primero por el nombre (orden alfabético)
                                            if (a.nombre < b.nombre) return -1;
                                            if (a.nombre > b.nombre) return 1;

                                            // Si los nombres son iguales, comparar por número (orden numérico)
                                            return a.numero - b.numero;
                                        })
                                        .map((elemento, index) => {
                                            const nombre = elemento.nombre;
                                            const apellido = elemento.descripcion
                                                ? elemento.descripcion
                                                : elemento.numero;
                                            return (
                                                <Tab
                                                    Key={elemento.id}
                                                    value={index}
                                                    label={nombre + " " + apellido}
                                                    sx={{ fontSize: "1.20 rem" }}
                                                    onClick={() =>
                                                        handleTabElementoGeneralClick(elemento)
                                                    }
                                                />
                                            );
                                        })}
                                </Tabs>
                                {elementosGeneral
                                    .sort((a, b) => {
                                        // Comparar primero por el nombre (orden alfabético)
                                        if (a.nombre < b.nombre) return -1;
                                        if (a.nombre > b.nombre) return 1;

                                        // Si los nombres son iguales, comparar por número (orden numérico)
                                        return a.numero - b.numero;
                                    })
                                    .map((elemento, index) => {
                                        return (
                                            <TabPanel
                                                key={elemento.id}
                                                value={tabActivoElementosGeneral}
                                                index={index}
                                            >
                                                <TableContainer
                                                    component={Paper}
                                                    sx={{
                                                        overflowX: "unset",
                                                        minWidth: 1450,
                                                    }}
                                                >
                                                    <Table sx={{ minWidth: 1200 }}>
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell
                                                                    sx={{
                                                                        position: "sticky",
                                                                        left: -20,
                                                                        zIndex: 2,
                                                                        backgroundColor: "white", // importante para que no se transparente
                                                                        width: "50px"
                                                                    }}
                                                                ></TableCell>
                                                                <TableCell
                                                                    align="left"
                                                                    sx={{
                                                                        position: "sticky",
                                                                        left: 50, // ancho acumulado de la primera col
                                                                        zIndex: 2,
                                                                        backgroundColor: "white",
                                                                        width: "150px"
                                                                    }}
                                                                >
                                                                    Tipo de análisis
                                                                </TableCell>
                                                                {/* NBG INI: Cabeceras dinámicas según periodo de oferta */}
                                                                {mesesAMostrar.map((mesInfo, index) => (
                                                                    <TableCell key={index} sx={{ width: "100px" }}>
                                                                        {mesInfo.nombre}
                                                                    </TableCell>
                                                                ))}
                                                                {/* NBG FIN */}
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {elementoGeneralSeleccionado &&
                                                                // Mapeamos todos los parametros
                                                                analisis.map((row) => {
                                                                    // Obtenemos todos los valores del parametro actual (valores del mismo parametro, enero, febrero, ...)
                                                                    const valoresPorTarea =
                                                                        parametrosFiltrados.filter(
                                                                            (analisis) =>
                                                                                parseInt(
                                                                                    analisis.analisis,
                                                                                    10
                                                                                ) === row.id
                                                                        );

                                                                    // NBG INI: Arrays dinámicos según meses de oferta
                                                                    let fechas = new Array(mesesAMostrar.length).fill(-1);
                                                                    let valorFechas = new Array(mesesAMostrar.length).fill(-1);
                                                                    let valorFechasRealizado = new Array(mesesAMostrar.length).fill(-1);
                                                                    // NBG FIN

                                                                    if (valoresPorTarea.length > 0) {
                                                                        valoresPorTarea.map((val) => {
                                                                            const fecha = new Date(val.fecha);

                                                                            // NBG INI: Buscar coincidencia por mes y año
                                                                            const indice = mesesAMostrar.findIndex(
                                                                                mesInfo => mesInfo.mes === fecha.getMonth() && mesInfo.anio === fecha.getFullYear()
                                                                            );

                                                                            if (indice !== -1) {
                                                                                valorFechas[indice] = fecha;

                                                                                if (val.incorrecto || val.noValido) {
                                                                                    fechas[indice] = 2;
                                                                                    valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                                                } else if (val.realizado && val.pdf) {
                                                                                    // Marcar como realizado (verde) si se ha marcado realizado (1 VERDE = PDF)
                                                                                    fechas[indice] = 1;
                                                                                    valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                                                } else if (val.realizado && row.tipo === 1) {
                                                                                    // Si está realizado y es tipo 1, mostrar verde aunque no tenga PDF
                                                                                    fechas[indice] = 1;
                                                                                    valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                                                } else if ((val.pdf=== null || val.pdf === 0) && val.realizado && row.tipo !== 1) {
                                                                                    // Si solo está recogido pero no realizado, mantener el azul (3 AZUL = NO PDF)
                                                                                    // Solo para tipo 0 y 2
                                                                                    fechas[indice] = 3;
                                                                                    valorFechasRealizado[indice] = new Date(val.fechaRealizado);
                                                                                } else {
                                                                                    fechas[indice] = 0;
                                                                                }
                                                                            }
                                                                            // NBG FIN
                                                                        });
                                                                    }

                                                                    // Devolvemos los valores
                                                                    return (
                                                                        valoresPorTarea.length > 0 && (
                                                                            <TableRow
                                                                                key={row.id}
                                                                                sx={{
                                                                                    "&:last-child td, &:last-child th":
                                                                                        { border: 0 },
                                                                                }}
                                                                            >
                                                                                <TableCell
                                                                                    sx={{
                                                                                        position: "sticky",
                                                                                        left: -20,
                                                                                        zIndex: 1,
                                                                                        backgroundColor: "white"
                                                                                    }}
                                                                                >
                                                                                    <Tooltip title="Ver parametros del elemento" placement="top">
                                                                                        <IconButton onClick={() => handleSeleccionarAnalisis(row.id)}>
                                                                                            <TimelineIcon />
                                                                                        </IconButton>
                                                                                    </Tooltip>
                                                                                </TableCell>
                                                                                <TableCell
                                                                                    align="left"
                                                                                    sx={{
                                                                                        position: "sticky",
                                                                                        left: 50, // igual que en el header
                                                                                        zIndex: 1,
                                                                                        backgroundColor: "white"
                                                                                    }}
                                                                                >
                                                                                    {row.nombre}
                                                                                </TableCell>
                                                                                {fechas.map((fecha, index) => (
                                                                                    <TableCell key={index}>
                                                                                        <Tooltip title={getTooltipMessage(fecha, row.tipo)}>
                                                                                            <span>
                                                                                                <IconButton
                                                                                                    onClick={(e) => {
                                                                                                        if (fecha === 0) {
                                                                                                            // caso error → navegar a Mantenimiento Técnico
                                                                                                            navigate("/mantenimientotecnico", {
                                                                                                                state: {
                                                                                                                    idElemento: elementoGeneralSeleccionado.id,
                                                                                                                    idAnalisis: row.id,
                                                                                                                    fecha: formatForApiLocalMidnight(valorFechas[index]), // "YYYY-MM-DDT00:00:00"
                                                                                                                    fechaIso: formatForUI(valorFechas[index]),
                                                                                                                    nombreAnalisis: row.nombre,
                                                                                                                    nombreElemento: elementoGeneralSeleccionado.nombre !== "undefined"
                                                                                                                        ? elementoGeneralSeleccionado.nombre + " " + (elementoGeneralSeleccionado.descripcion || elementoGeneralSeleccionado.numero)
                                                                                                                        : null,
                                                                                                                    codigoCliente: clienteSeleccionado.codigoCliente,
                                                                                                                    nombreCliente: clienteSeleccionado.nombreCliente,
                                                                                                                    oferta: clienteSeleccionado.oferta
                                                                                                                }
                                                                                                            });
                                                                                                        } else if (fecha === 3) { // <-- Corregido: else if
                                                                                                            navigate("/visualizacion", {
                                                                                                                state: {
                                                                                                                    idElemento: elementoGeneralSeleccionado.id,
                                                                                                                    idAnalisis: row.id,
                                                                                                                    fecha: formatForApiLocalMidnight(valorFechas[index]), // "YYYY-MM-DDT00:00:00"
                                                                                                                    fechaIso: formatForUI(valorFechas[index]),
                                                                                                                    nombreAnalisis: row.nombre,
                                                                                                                    nombreElemento: elementoGeneralSeleccionado.descripcion !== "" ? elementoGeneralSeleccionado.nombre + " " + elementoGeneralSeleccionado.descripcion : elementoGeneralSeleccionado.nombre + " " + elementoGeneralSeleccionado.numero,
                                                                                                                    codigoCliente: clienteSeleccionado.codigoCliente,
                                                                                                                    nombreCliente: clienteSeleccionado.nombreCliente,
                                                                                                                    oferta: clienteSeleccionado.oferta
                                                                                                                }
                                                                                                            });

                                                                                                        } else {
                                                                                                            // click downloads the PDF
                                                                                                            descargarPdf(row.id, valorFechas[index]);
                                                                                                        }
                                                                                                    }}
                                                                                                    sx={{
                                                                                                        color: fecha === 3 ? '#2196f3' : undefined, // azul solo cuando fecha === 3
                                                                                                        '&.Mui-disabled': (row.tipo === 1 && fecha === 1) ? { color: 'success.main' } : {}
                                                                                                    }}
                                                                                                    color={
                                                                                                        fecha === -1
                                                                                                            ? "primary"
                                                                                                            : fecha === 0
                                                                                                                ? "error"
                                                                                                                : fecha === 2
                                                                                                                    ? "warning"
                                                                                                                    : fecha === 1
                                                                                                                        ? "success"
                                                                                                                        : undefined
                                                                                                    }
                                                                                                    disabled={fecha === -1 || (row.tipo === 1 && fecha === 1)}
                                                                                                >

                                                                                                    {fecha === -1 ? (
                                                                                                        <RemoveIcon />
                                                                                                    ) : fecha === 0 ? (
                                                                                                        <ClearIcon />
                                                                                                    ) : fecha === 2 ? (
                                                                                                        <WarningIcon />
                                                                                                    ) : fecha === 3 ? (
                                                                                                        <CheckIcon />
                                                                                                    ) : (
                                                                                                        <CheckIcon />
                                                                                                    )}
                                                                                                </IconButton>
                                                                                            </span>
                                                                                        </Tooltip>
                                                                                        {fecha === -1 ? (
                                                                                            <p></p>
                                                                                        ) : valorFechasRealizado[
                                                                                            index
                                                                                        ] === -1 ? (
                                                                                            <p>
                                                                                                {valorFechas[index] !== -1
                                                                                                    ? valorFechas[
                                                                                                        index
                                                                                                    ].toLocaleDateString()
                                                                                                    : ""}
                                                                                            </p>
                                                                                        ) : (
                                                                                            <p>
                                                                                                {valorFechasRealizado[
                                                                                                    index
                                                                                                ] !== -1
                                                                                                    ? valorFechasRealizado[
                                                                                                        index
                                                                                                    ].toLocaleDateString()
                                                                                                    : ""}
                                                                                            </p>
                                                                                        )}
                                                                                    </TableCell>
                                                                                ))}
                                                                            </TableRow>
                                                                        )
                                                                    );
                                                                })}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </TabPanel>
                                        );
                                    })}
                            </Grid>
                        ) : (
                            <></>
                        )}
                    </Grid>
                </CardContent>
            </Card >
        </>
    )
}